import type { Ship } from "../ship/Ship";
import type { Pad } from "../track/Track";

/** Seconds before the same craft can fire the same pad again. */
const PAD_COOLDOWN = 1.5;
/** How far (world units) a craft can sit above/below a pad and still be on its
 * level — so a pad on one deck of a crossing never fires for the other deck. */
const PAD_LEVEL_TOLERANCE = 10;

/**
 * Fire every pad this craft drove over this frame. The test is the path from
 * its previous to its current position against the pad's footprint, so a fast
 * craft can't step over a pad between frames (low frame rates, boost), and the
 * cooldowns are per craft, so racers crossing the same pad close together all
 * get it.
 */
export function triggerPads(ship: Ship, pads: readonly Pad[], dt: number): void {
  for (const pad of pads) {
    const cd = ship.padCooldowns.get(pad) ?? 0;
    if (cd > 0) {
      ship.padCooldowns.set(pad, cd - dt);
      continue;
    }
    if (ship.padLockout > 0 || !crossesPad(ship, pad)) continue;
    if (pad.kind === "boost") ship.applyBoostPad(pad.forward);
    else ship.applyJumpPad(pad.power, pad.forward);
    ship.padCooldowns.set(pad, PAD_COOLDOWN);
  }
}

function crossesPad(ship: Ship, pad: Pad): boolean {
  if (ship.airborne) return false;
  // Pad-local axes in the road surface: v along its forward, u across it (+ =
  // right, the same sense as the road's right), and height off its face.
  const f = pad.forward;
  const up = pad.up;
  const rx = up.y * f.z - up.z * f.y;
  const ry = up.z * f.x - up.x * f.z;
  const rz = up.x * f.y - up.y * f.x;
  const x1 = ship.position.x - pad.position.x;
  const y1 = ship.position.y - pad.position.y;
  const z1 = ship.position.z - pad.position.z;
  if (Math.abs(x1 * up.x + y1 * up.y + z1 * up.z) > PAD_LEVEL_TOLERANCE) return false;
  const x0 = ship.prevPosition.x - pad.position.x;
  const y0 = ship.prevPosition.y - pad.position.y;
  const z0 = ship.prevPosition.z - pad.position.z;
  return segmentHitsBox(
    x0 * rx + y0 * ry + z0 * rz,
    x0 * f.x + y0 * f.y + z0 * f.z,
    x1 * rx + y1 * ry + z1 * rz,
    x1 * f.x + y1 * f.y + z1 * f.z,
    pad.halfWidth,
    pad.halfLength
  );
}

/** Does the segment (u0,v0)→(u1,v1) touch the box |u| ≤ hu, |v| ≤ hv?
 * (Liang–Barsky clipping.) */
function segmentHitsBox(u0: number, v0: number, u1: number, v1: number, hu: number, hv: number): boolean {
  let t0 = 0;
  let t1 = 1;
  const clip = (p: number, q: number): boolean => {
    if (p === 0) return q >= 0;
    const r = q / p;
    if (p < 0) {
      if (r > t1) return false;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return false;
      if (r < t1) t1 = r;
    }
    return true;
  };
  const du = u1 - u0;
  const dv = v1 - v0;
  return clip(-du, u0 + hu) && clip(du, hu - u0) && clip(-dv, v0 + hv) && clip(dv, hv - v0);
}
